/**
 * Created by Pradeep Kumar on 5/20/2019.
 */

import {Background} from "@nolimitcity/slot-game/bin/core/background/Background";
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {GameModeEvent} from "@nolimitcity/slot-game/bin/core/gamemode/event/GameModeEvent";
import {GameMode, IGameModeChangedData} from "@nolimitcity/slot-game/bin/core/gamemode/GameMode";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {Orientation} from "@nolimitcity/slot-game/bin/core/stage/Orientation";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {IResizeData, StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {TimelineLite, TweenLite, TweenMax} from "gsap";
import {TombOfNefertitiMultiplierEvents} from "../multiplierIndicator/TombOfNefertitiMultiplier";
import {TombOfNefertitiFreeSpinIntroView} from "../screen/freespinintro/TombOfNefertitiFreeSpinIntroView";
import {TombOfNefertitiGameAssets} from "../TombOfNefertitiGameAssets";
import {TombOfNefertitiGameConfig} from "../TombOfNefertitiGameConfig";
import {TombOfNefertitiSoundConfig} from "../TombOfNefertitiSoundConfig";

export class TombOfNefertitiBackground extends Background {
    private _mainBackground:PIXI.Sprite;
    private _fsBackground:PIXI.Sprite;
    private _fsBGDarkeningLayer:PIXI.Sprite;

    private _freeSpinsAnim:TimelineSprite[] = [];
    private _freeSpinsAnimPortrait:TimelineSprite[] = [];

    private _animatingBg:PIXI.Sprite;
    private _torchStandPortrait:PIXI.Sprite;

    private _reelFrame:PIXI.Sprite;
    private _timeline:TimelineLite | null = null;

    private _orientation:string = "";
    private _isSwitchingAllowed:boolean = true;

    static HOLD_BG_TRANSITION:string = 'holdSwitchingToFSBG';
    static TENSE_BG_FOR_ATTENTION:string = 'tenseBGForAttention';
    static CLEAR_TENSE_BG_FOR_ATTENTION:string = 'clearTenseBGForAttention';

    private _lastUpdatedMul:number = 0;
    private _multiplier:number = 0;

    public static _reelsFrame:PIXI.Sprite;

    private _animatingBgFromAlpha:number;

    protected initAnimations():void {
        super.initAnimations();

        this._fsBGDarkeningLayer = new PIXI.Sprite();
        this._fsBackground = this.createBgItem(TombOfNefertitiGameAssets.BONUS_GAME_BACKGROUND);
        this._mainBackground = this.createBgItem(TombOfNefertitiGameAssets.BACKGROUND);

        const width:number = this._fsBackground.width;

        let darkeningGraphics:PIXI.Graphics = new PIXI.Graphics();
        darkeningGraphics.beginFill(0x000000);
        darkeningGraphics.drawRect(-width / 2, -width / 2, width, width); // need the graphics to be of 1680 * 1680
        darkeningGraphics.endFill();
        this._fsBGDarkeningLayer.addChild((darkeningGraphics));
        this._fsBackground.addChild(this._fsBGDarkeningLayer);
        this._fsBGDarkeningLayer.visible = false;

        const reelBackgroundLayer:ScalableLayer = StageManager.getLayer(TombOfNefertitiGameConfig.instance.LAYERS.REEL_BACKGROUND.name);

        this._reelFrame = this.createReelBgItem(TombOfNefertitiGameAssets.REELS_FRAME);
        reelBackgroundLayer.addChildAt(this._reelFrame, 0);

        TombOfNefertitiBackground._reelsFrame = this._reelFrame;

        this.createFSBgAnimations();
        this.createFSBgAnimationsPortrait();
    }

    private fadeForAttention():void {
        this._mainBackground.addChild(this._fsBGDarkeningLayer);
        this._fsBGDarkeningLayer.alpha = 0;
        this._fsBGDarkeningLayer.visible = true;
        TweenLite.to(this._fsBGDarkeningLayer, 0.5, {alpha : 0.2});
        TweenLite.to(this._mainBackground.scale, 0.5, {x : 1.1, y : 1.1});
    }

    private clearAttention():void {
        TweenLite.to(this._fsBGDarkeningLayer, 0.5, {
            alpha : 0,
            onComplete : () => {
                this._fsBGDarkeningLayer.visible = false;
                this._fsBackground.addChild(this._fsBGDarkeningLayer);
            }
        });
        TweenLite.to(this._mainBackground.scale, 0.5, {x : 1, y : 1});
    }

    protected onResize(resizeData:IResizeData):void {
        const screenWidth = Math.ceil(resizeData.width / this._layer.scale.x);
        const screenHeight = Math.ceil(resizeData.height / this._layer.scale.y);
        const leftOffset = -this._layer.x / this._layer.scale.x;
        const topOffset = -this._layer.y / this._layer.scale.y;

        if(this._mainBackground.height >= screenHeight) {
            this._mainBackground.position.set(screenWidth / 2 + leftOffset, screenHeight / 2 + topOffset);
            this._fsBackground.position.set(screenWidth / 2 + leftOffset, screenHeight / 2 + topOffset);
        } else {
            this._mainBackground.position.set(screenWidth / 2 + leftOffset, this._mainBackground.height / 2 + topOffset);
            this._fsBackground.position.set(screenWidth / 2 + leftOffset, this._fsBackground.height / 2 + topOffset);
        }

        this._animatingBg.position.set(360, 360);

        if(resizeData.orientation === Orientation.PORTRAIT) {
            this._animatingBg.texture = GameResources.getTextures("bgGoldAnimationPortrait")[0];
            this._animatingBg.position.set(screenWidth / 2 + leftOffset, 795);
            this._animatingBgFromAlpha = 0.3;
        } else {
            this._animatingBg.texture = GameResources.getTextures("bgGoldAnimation")[0];
            this._animatingBgFromAlpha = 1;
        }

        if(this._currentMode && this._currentMode.indexOf(GameMode.FREESPIN) !== -1) {
            if(resizeData.orientation === Orientation.PORTRAIT) {
                this._torchStandPortrait.visible = true;
                for(let i:number = 0; i < this._freeSpinsAnimPortrait.length; ++i) {
                    this._freeSpinsAnimPortrait[i].visible = true;
                }

                for(let i:number = 0; i < this._freeSpinsAnim.length; ++i) {
                    this._freeSpinsAnim[i].visible = false;
                }
            } else {
                for(let i:number = 0; i < this._freeSpinsAnim.length; ++i) {
                    this._freeSpinsAnim[i].visible = true;
                }

                this._torchStandPortrait.visible = false;
                for(let i:number = 0; i < this._freeSpinsAnimPortrait.length; ++i) {
                    this._freeSpinsAnimPortrait[i].visible = false;
                }
            }
        }

        this._orientation = resizeData.orientation;
    }

    protected onChangeBackground(data:IGameModeChangedData):boolean {
        let isModeChanged:boolean = super.onChangeBackground(data);

        if(isModeChanged) {
            if(this._currentMode.indexOf(GameMode.FREESPIN) === -1) {
                this.onResize(this._resizeData);
                this._mainBackground.alpha = 1;
                this._mainBackground.scale.set(1);
                this._mainBackground.visible = true;

                // fs items
                this._fsBackground.visible = false;
                this._animatingBg.visible = false;
                this._torchStandPortrait.visible = false;

                this._freeSpinsAnim[this._freeSpinsAnim.length - 1].visible = false;
                this._freeSpinsAnim[this._freeSpinsAnim.length - 2].visible = false;
                for(let i:number = 0; i < this._freeSpinsAnim.length - 2; ++i) {
                    this._freeSpinsAnim[i].visible = false;
                    this._freeSpinsAnim[i].stopLoop();
                }
                for(let i:number = 0; i < this._freeSpinsAnimPortrait.length; ++i) {
                    this._freeSpinsAnimPortrait[i].visible = false;
                    this._freeSpinsAnimPortrait[i].stopLoop();
                }

                this._timeline = null;
            } else {
                if(this._isSwitchingAllowed) {
                    this._fsBackground.alpha = 1;
                    this._fsBackground.visible = true;
                    this._fsBGDarkeningLayer.visible = false;
                    this._mainBackground.alpha = 0;
                    this._mainBackground.scale.set(1);
                    this._mainBackground.visible = false;
                    this.litTorch();
                    this.onMultiplierUpdated(this._multiplier);
                }
            }
        }

        return isModeChanged;
    }

    protected addEventListeners():void {
        super.addEventListeners();

        EventHandler.addEventListener(this, TombOfNefertitiFreeSpinIntroView.FREESPIN_INTRO_TRANSITION_IN, (event:GameEvent) => {
            this.fadeReels(event.params[0])
        });
        EventHandler.addEventListener(this, TombOfNefertitiFreeSpinIntroView.FREESPIN_INTRO_REVEAL_REELS, (event:GameEvent) => {
            this.revealReels()
        });
        EventHandler.addEventListener(this, TombOfNefertitiMultiplierEvents.MULTIPLIER_UPDATED, (event:GameEvent) => this.onMultiplierUpdated(event.params[0]));
        EventHandler.addEventListener(this, TombOfNefertitiBackground.HOLD_BG_TRANSITION, (event:GameEvent) => this.holdBGSwitching());


        EventHandler.addEventListener(this, TombOfNefertitiBackground.TENSE_BG_FOR_ATTENTION, (event:GameEvent) => this.fadeForAttention());
        EventHandler.addEventListener(this, TombOfNefertitiBackground.CLEAR_TENSE_BG_FOR_ATTENTION, (event:GameEvent) => this.clearAttention());
    }

    private holdBGSwitching() {
        this._isSwitchingAllowed = false;
    }

    private litTorch():void {
        let duration:number = 23 / 30;

        this._timeline = new TimelineLite();

        this._freeSpinsAnim[0].position.set(-228, 193);
        this._freeSpinsAnim[1].position.set(953, 193);
        for(let i:number = 0; i < this._freeSpinsAnim.length - 2; ++i) {
            this._freeSpinsAnim[i].scale.set(0.5 * 2);
            (this._freeSpinsAnim[i] as any)._textures = GameResources.getTextures("fireLevel1Loop");
            this._freeSpinsAnim[i].playLoop();
        }

        // portrait
        this._freeSpinsAnimPortrait[0].position.set(50, 665);
        this._freeSpinsAnimPortrait[1].position.set(670, 665);

        for(let i:number = 0; i < this._freeSpinsAnimPortrait.length; ++i) {
            this._freeSpinsAnimPortrait[i].scale.set(0.35 * 2);
            (this._freeSpinsAnimPortrait[i] as any)._textures = GameResources.getTextures("fireLevel1Loop");
            this._freeSpinsAnimPortrait[i].playLoop();
        }

        if(this._orientation === Orientation.PORTRAIT) {
            this._torchStandPortrait.visible = true;
            for(let i:number = 0; i < this._freeSpinsAnimPortrait.length; ++i) {
                this._freeSpinsAnimPortrait[i].visible = true;
            }
            for(let i:number = 0; i < this._freeSpinsAnim.length; ++i) {
                this._freeSpinsAnim[i].visible = false;
            }

            this._animatingBg.texture = GameResources.getTextures("bgGoldAnimationPortrait")[0];
            this._animatingBgFromAlpha = 0.3;
        } else {
            for(let i:number = 0; i < this._freeSpinsAnim.length; ++i) {
                this._freeSpinsAnim[i].visible = true;
            }
            this._torchStandPortrait.visible = false;
            for(let i:number = 0; i < this._freeSpinsAnimPortrait.length; ++i) {
                this._freeSpinsAnimPortrait[i].visible = false;
            }

            this._animatingBg.texture = GameResources.getTextures("bgGoldAnimation")[0];
            this._animatingBgFromAlpha = 1;
        }

        this._animatingBg.visible = true;
        TweenMax.fromTo(this._animatingBg, duration, {alpha : this._animatingBgFromAlpha}, {
            alpha : 0,
            repeat : -1,
            yoyo : true
        });
    }

    private fadeReels(direction:string):void {
        if(direction === 'OUT') {
            return;
        }
        TweenLite.to(StageManager.getLayer(TombOfNefertitiGameConfig.instance.LAYERS.REEL_BACKGROUND.name), 1.4, {
            alpha : 0,
            onComplete : () => {
                setTimeout(this.enterTheTomb.bind(this), 500)
            }
        });
    }

    private revealReels():void {
        EventHandler.dispatchEvent(new GameEvent(GameModeEvent.CHANGE_MODE, {newMode : GameMode.FREESPIN}));
        setTimeout(() => {this._isSwitchingAllowed = true}, 10);
        TweenLite.to(StageManager.getLayer(TombOfNefertitiGameConfig.instance.LAYERS.REEL_BACKGROUND.name), 1, {
            alpha : 1,
            onComplete : this.revealNewBetLines.bind(this)
        });
    }

    private revealNewBetLines():void {
        EventHandler.dispatchEvent(new GameEvent(TombOfNefertitiFreeSpinIntroView.FREESPIN_INTRO_REVEAL_BETLINES));
    }

    private enterTheTomb():void {
        this._fsBackground.alpha = 1;
        this._fsBackground.visible = true;
        this._fsBGDarkeningLayer.visible = true;
        this._fsBGDarkeningLayer.alpha = 0.80;
        TweenLite.to(this._mainBackground.scale, 1.8, {x : 3.5, y : 3});
        TweenLite.to(this._mainBackground, 1.8, {y : -750});
        TweenLite.to(this._mainBackground, 2.0, {alpha : 0, onComplete : this.revealBonusReels.bind(this)});
    }

    private revealBonusReels():void {
        this.litTorch();
        TweenLite.to(this._fsBGDarkeningLayer, 1.5, {alpha : 0});
        EventHandler.dispatchEvent(new GameEvent(TombOfNefertitiFreeSpinIntroView.FREESPIN_INTRO_SHOW_BANNER));
    }

    private createReelBgItem(name:string):PIXI.Sprite {
        let item:PIXI.Sprite = new PIXI.Sprite(GameResources.getTextures(name)[0]),
            offsetY:number = 24,
            offsetX:number = 1;

        item.anchor.set(0.5);
        item.position.set(TombOfNefertitiGameConfig.instance.GAME_WIDTH / 2 + offsetX, item.height / 2 + offsetY);

        return item;
    }

    private createBgItem(name:string):PIXI.Sprite {
        let item:PIXI.Sprite = new PIXI.Sprite(GameResources.getTextures(name)[0]);

        item.anchor.set(0.5);
        this.addChild(item);

        return item;
    }

    private createFSBgAnimations():void {
        let animatingBg:PIXI.Sprite = new PIXI.Sprite(GameResources.getTextures("bgGoldAnimation")[0]),
            leftStand:PIXI.Sprite = new PIXI.Sprite(GameResources.getTextures("bgAnimations/standLeftLandscape")[0]),
            rightStand:PIXI.Sprite = new PIXI.Sprite(GameResources.getTextures("bgAnimations/standRightLandscape")[0]),
            leftFlame:TimelineSprite = new TimelineSprite(GameResources.getTextures("fireLevel1Loop"), 25),
            rightFlame:TimelineSprite = new TimelineSprite(GameResources.getTextures("fireLevel1Loop"), 22);

        leftStand.position.set(-228, 450);
        rightStand.position.set(954, 475);

        leftFlame.blendMode = PIXI.BLEND_MODES.ADD;
        rightFlame.blendMode = PIXI.BLEND_MODES.ADD;

        this._layer.addChild(animatingBg);
        this._layer.addChild(leftStand);
        this._layer.addChild(rightStand);
        this._layer.addChild(leftFlame);
        this._layer.addChild(rightFlame);

        this._freeSpinsAnim.push(leftFlame);
        this._freeSpinsAnim.push(rightFlame);
        this._freeSpinsAnim.push(<TimelineSprite>leftStand);
        this._freeSpinsAnim.push(<TimelineSprite>rightStand);

        animatingBg.anchor.set(0.5);
        animatingBg.visible = false;
        this._animatingBg = animatingBg;

        for(let i:number = 0; i < this._freeSpinsAnim.length; ++i) {
            this._freeSpinsAnim[i].visible = false;
            this._freeSpinsAnim[i].anchor.set(0.5);
        }
    }

    private createFSBgAnimationsPortrait():void {
        let torchStand:PIXI.Sprite = new PIXI.Sprite(GameResources.getTextures("bgAnimations/standPortrait")[0]),
            bottomLeftFlame:TimelineSprite = new TimelineSprite(GameResources.getTextures("fireLevel1Loop"), 25),
            bottomRightFlame:TimelineSprite = new TimelineSprite(GameResources.getTextures("fireLevel1Loop"), 22);

        torchStand.position.set(360, 852);
        torchStand.anchor.set(0.5);
        torchStand.visible = false;
        this._torchStandPortrait = torchStand;

        bottomLeftFlame.blendMode = PIXI.BLEND_MODES.ADD;
        bottomRightFlame.blendMode = PIXI.BLEND_MODES.ADD;

        this._layer.addChild(torchStand);
        this._layer.addChild(bottomLeftFlame);
        this._layer.addChild(bottomRightFlame);

        this._freeSpinsAnimPortrait.push(bottomLeftFlame);
        this._freeSpinsAnimPortrait.push(bottomRightFlame);

        for(let i:number = 0; i < this._freeSpinsAnimPortrait.length; ++i) {
            this._freeSpinsAnimPortrait[i].visible = false;
            this._freeSpinsAnimPortrait[i].anchor.set(0.5);
        }
    }

    private onMultiplierUpdated(value:number):void {
        this._timeline = new TimelineLite();

        this._multiplier = value;

        if(value >= 10) {
            if(this._lastUpdatedMul === 10) {
                return;
            }
            SlotGame.sound.playEffect(TombOfNefertitiSoundConfig.instance.FIRE_BURST);

            this._lastUpdatedMul = 10;
            for(let i:number = 0; i < this._freeSpinsAnim.length - 2; ++i) {
                this._freeSpinsAnim[i].y -= 40;
                this._freeSpinsAnim[i].scale.set(0.65 * 2);
                (this._freeSpinsAnim[i] as any)._textures = GameResources.getTextures("fireLevel3Start");
                this._freeSpinsAnim[i].getAnimation();
            }

            this._timeline.add(() => {
                for(let i:number = 0; i < this._freeSpinsAnim.length - 2; ++i) {
                    (this._freeSpinsAnim[i] as any)._textures = GameResources.getTextures("fireLevel3Loop");
                    this._freeSpinsAnim[i].playLoop();
                }
            }, (67 / 25));

            // portrait
            for(let i:number = 0; i < this._freeSpinsAnimPortrait.length; ++i) {
                this._freeSpinsAnimPortrait[i].y -= 35;
                this._freeSpinsAnimPortrait[i].scale.set(0.42 * 2);
                (this._freeSpinsAnimPortrait[i] as any)._textures = GameResources.getTextures("fireLevel3Start");
                this._freeSpinsAnimPortrait[i].getAnimation();
            }

            this._timeline.add(() => {
                for(let i:number = 0; i < this._freeSpinsAnimPortrait.length; ++i) {
                    (this._freeSpinsAnimPortrait[i] as any)._textures = GameResources.getTextures("fireLevel3Loop");
                    this._freeSpinsAnimPortrait[i].playLoop();
                }
            }, (67 / 25));
        } else if(value >= 5) {
            if(this._lastUpdatedMul === 5) {
                return;
            }
            SlotGame.sound.playEffect(TombOfNefertitiSoundConfig.instance.FIRE_BURST);

            this._lastUpdatedMul = 5;
            for(let i:number = 0; i < this._freeSpinsAnim.length - 2; ++i) {
                (this._freeSpinsAnim[i] as any)._textures = GameResources.getTextures("fireLevel2Start");
                this._freeSpinsAnim[i].getAnimation();
            }

            this._timeline.add(() => {
                for(let i:number = 0; i < this._freeSpinsAnim.length - 2; ++i) {
                    (this._freeSpinsAnim[i] as any)._textures = GameResources.getTextures("fireLevel2Loop");
                    this._freeSpinsAnim[i].playLoop();
                }
            }, (34 / 25));

            // portrait
            for(let i:number = 0; i < this._freeSpinsAnimPortrait.length; ++i) {
                this._freeSpinsAnimPortrait[i].y += 15;
                this._freeSpinsAnimPortrait[i].scale.set(0.3 * 2);
                (this._freeSpinsAnimPortrait[i] as any)._textures = GameResources.getTextures("fireLevel2Start");
                this._freeSpinsAnimPortrait[i].getAnimation();
            }

            this._timeline.add(() => {
                for(let i:number = 0; i < this._freeSpinsAnimPortrait.length; ++i) {
                    (this._freeSpinsAnimPortrait[i] as any)._textures = GameResources.getTextures("fireLevel2Loop");
                    this._freeSpinsAnimPortrait[i].playLoop();
                }
            }, (34 / 25));
        }
    }
}